import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import AddPhotoAlternateOutlined from "@mui/icons-material/AddPhotoAlternateOutlined";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import {
  imageFingerprint,
  MAX_PHOTOS,
  processImage,
  validateImage,
} from "./imageProcessing";
import type { LocalPhoto } from "./types";

export default function PhotoUploader({
  photos,
  onChange,
  onBusy,
  validationError,
}: {
  photos: LocalPhoto[];
  onChange: (photos: LocalPhoto[]) => void;
  onBusy: (busy: boolean) => void;
  validationError?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const replacement = useRef<number | null>(null);
  const mounted = useRef(true);
  const busyRef = useRef(false);
  const [processing, setProcessing] = useState("");
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const choose = (index: number | null = null) => {
    replacement.current = index;
    if (input.current) {
      input.current.multiple = index === null;
      input.current.click();
    }
  };
  const add = async (files: File[], replace: number | null) => {
    if (busyRef.current || !files.length) return;
    if (
      (replace === null && photos.length + files.length > MAX_PHOTOS) ||
      (replace !== null && files.length !== 1)
    ) {
      setError(
        "Choose up to three photos in total. Replace one photo at a time.",
      );
      return;
    }
    busyRef.current = true;
    onBusy(true);
    setError("");
    const next = [...photos];
    const errors: string[] = [];
    for (const file of files) {
      if (!mounted.current) break;
      setProcessing(file.name);
      try {
        validateImage(file);
        const fingerprint = await imageFingerprint(file);
        if (
          next.some(
            (photo, index) =>
              index !== replace && photo.fingerprint === fingerprint,
          )
        ) {
          throw new Error("This photo has already been added.");
        }
        const photo = await processImage(file, fingerprint);
        if (!mounted.current) {
          URL.revokeObjectURL(photo.previewUrl);
          break;
        }
        if (replace !== null) {
          URL.revokeObjectURL(next[replace].previewUrl);
          next[replace] = photo;
        } else next.push(photo);
      } catch (failure) {
        errors.push(
          `${file.name}: ${failure instanceof Error ? failure.message : "This photo couldn't be processed. Try another image."}`,
        );
      }
    }
    if (mounted.current) {
      onChange(next);
      setProcessing("");
      setError(errors.join(" "));
      onBusy(false);
    } else {
      next
        .filter((photo) => !photos.includes(photo))
        .forEach((photo) => URL.revokeObjectURL(photo.previewUrl));
    }
    busyRef.current = false;
  };
  const move = (from: number, to: number) => {
    const next = [...photos];
    const [photo] = next.splice(from, 1);
    next.splice(to, 0, photo);
    onChange(next);
  };
  return (
    <Stack
      spacing={2}
      sx={{ "& .MuiIconButton-root": { minWidth: 44, minHeight: 44 } }}
    >
      <Typography fontWeight={650} color="secondary.main">
        Toy photos *
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Add clear photos of your toy, its packaging, and any details buyers
        should know about. For collectibles, show box condition, labels, wear,
        and original accessories.
      </Typography>
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
        multiple
        hidden
        aria-label="Choose toy photo files"
        disabled={!!processing}
        onChange={(event) => {
          const files = Array.from(event.target.files || []);
          event.target.value = "";
          void add(files, replacement.current);
        }}
      />
      <Box
        role="group"
        aria-label="Toy photo drop area"
        onDragOver={(event) => {
          event.preventDefault();
          if (!processing) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          replacement.current = null;
          void add(Array.from(event.dataTransfer.files), null);
        }}
        sx={{
          border: "2px dashed",
          borderColor: dragging ? "#ac4b20" : "#d8bba8",
          borderRadius: 3,
          bgcolor: dragging ? "#fff1e5" : "#fffbf7",
          p: { xs: 2, sm: 3 },
          textAlign: "center",
        }}
      >
        <AddPhotoAlternateOutlined
          sx={{ fontSize: 36, color: "#a64c23", mb: 0.5 }}
        />
        <Typography fontWeight={650}>Give buyers a closer look</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Drop photos here, or choose from your device.
        </Typography>
        <Button
          type="button"
          variant="outlined"
          onClick={() => choose()}
          disabled={!!processing || photos.length === MAX_PHOTOS}
        >
          Choose photos
        </Button>
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          JPG, PNG, WebP · Up to 3 photos · 5 MB each
        </Typography>
      </Box>
      {processing && (
        <Stack direction="row" spacing={1} alignItems="center" role="status">
          <CircularProgress size={20} />
          <Typography sx={{ overflowWrap: "anywhere" }}>
            Preparing {processing}...
          </Typography>
        </Stack>
      )}
      {(error || validationError) && (
        <Alert severity="error" role="alert">
          {error || validationError}
        </Alert>
      )}
      {!!photos.length && (
        <Grid container spacing={1.5}>
          {photos.map((photo, index) => (
            <Grid item xs={12} sm={4} key={photo.id}>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 3,
                  p: 1,
                  height: "100%",
                }}
              >
                <Box
                  component="img"
                  src={photo.previewUrl}
                  alt={`Toy photo ${index + 1}: ${photo.name}`}
                  sx={{
                    width: "100%",
                    height: 160,
                    objectFit: "contain",
                    bgcolor: "#f4f5f6",
                    borderRadius: 2,
                  }}
                />
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mt: 1 }}
                >
                  {index === 0 ? (
                    <Chip
                      label="Cover photo"
                      size="small"
                      sx={{ bgcolor: "#fff0e5", color: "#8c3f1b" }}
                    />
                  ) : (
                    <Button
                      size="small"
                      type="button"
                      disabled={!!processing}
                      onClick={() => move(index, 0)}
                      aria-label={`Make photo ${index + 1} the cover`}
                    >
                      Make cover
                    </Button>
                  )}
                  <IconButton
                    size="small"
                    type="button"
                    disabled={!!processing}
                    aria-label={`Remove photo ${index + 1}`}
                    onClick={() => {
                      URL.revokeObjectURL(photo.previewUrl);
                      onChange(
                        photos.filter((_, position) => position !== index),
                      );
                    }}
                  >
                    <DeleteOutlineRounded />
                  </IconButton>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Button
                    size="small"
                    type="button"
                    disabled={!!processing}
                    onClick={() => choose(index)}
                    aria-label={`Replace photo ${index + 1}`}
                  >
                    Replace
                  </Button>
                  <Box>
                    <IconButton
                      size="small"
                      type="button"
                      disabled={!!processing || index === 0}
                      aria-label={`Move photo ${index + 1} earlier`}
                      onClick={() => move(index, index - 1)}
                    >
                      <ArrowBackRounded fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      type="button"
                      disabled={!!processing || index === photos.length - 1}
                      aria-label={`Move photo ${index + 1} later`}
                      onClick={() => move(index, index + 1)}
                    >
                      <ArrowForwardRounded fontSize="small" />
                    </IconButton>
                  </Box>
                </Stack>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
      <Typography variant="caption" color="text.secondary">
        Photos are resized on this device, preserving their proportions and
        orientation. Nothing is uploaded. You'll need to reselect photos after
        refreshing.
      </Typography>
    </Stack>
  );
}
